import { put } from "../../common/api/apiShared";
import { LegalEntityContactRow, LegalEntityContactType } from "./LegalEntityContactTab.types";
import { ILegalEntityContactTab } from "./LegalEntityContactTab";
import { useTranslation } from "../../localization/useTranslation";
import { useState } from "react";
import { useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useLegalEntityContactsQuery } from "../useLegalEntityContactsQuery";
import { useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { getUniqueFilterOptions, getUniqueNestedFilterOptions, sortByEnumValue, sortByName } from "@common/hooks/filterUtils.ts";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { setFirstLetterToLowerCase } from "@common/stringOperations";
import { usePanes, useRefetchWhenPaneSaves } from "../../main/panes/usePanes";
import LegalEntityContactPane from "./legalEntityContactPane/LegalEntityContactPane";

export function useLegalEntityContactTabDataMapping(props: ILegalEntityContactTab) {
    const { translateString } = useTranslation();
    const snackbar = useDotLegalSnackbar();

    let { isLoading, data, refetch } = useLegalEntityContactsQuery(props.legalEntityId);

    const { selectFilters, booleanFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `legalEntity-${props.legalEntityId}-contacts-filters`
    );

    const allRows = data?.rows ?? [];
    const primaryContactFilter = booleanFilters.primaryContact ?? "";

    const contacts = applyClientFilters(
        allRows,
        [
            { id: "name", candidate: (row) => row.name },
            { id: "type", candidate: (row) => row.contactTypes.map((type) => type.toString()) },
        ],
        selectFilters,
        {},
        filterOperators,
        (row) => primaryContactFilter === "" || row.primaryContact === primaryContactFilter
    );

    const translateContactType = (contactType: LegalEntityContactType) =>
        translateString(setFirstLetterToLowerCase(LegalEntityContactType[contactType].toString()));

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "name",
                    label: translateString("name"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.name ?? [],
                    fixedData: getUniqueFilterOptions(allRows, (row) => row.name).sort(sortByName),
                },
                {
                    id: "type",
                    label: translateString("type"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.type ?? [],
                    fixedData: getUniqueNestedFilterOptions(
                        allRows,
                        (row) => row.contactTypes,
                        (type) => translateContactType(type),
                        (type) => type.toString()
                    ).sort(sortByEnumValue),
                },
                {
                    id: "primaryContact",
                    label: translateString("primaryContact"),
                    type: FilterOptionType.Boolean,
                    value: primaryContactFilter,
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(selectFilters).some((v) => v.length > 0) || primaryContactFilter !== "";
    const { open: openPane } = usePanes();
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [selectedContact, setSelectedContact] = useState<LegalEntityContactRow | undefined>(undefined);

    const editMutation = useDotLegalMutation(setPrimaryContactMutation);

    function setPrimaryContactMutation(doc: LegalEntityContactRow) {
        return put(`/LegalEntityContacts/${props.legalEntityId}/primaryContact/${doc.id}`, null);
    }

    const setPrimaryContact = (doc: LegalEntityContactRow) => {
        editMutation.mutateAsync(doc!, {
            onSuccess: (response: any) => {
                refetch();
                snackbar.show(translateString("primaryContactUpdated"));
            },
        });
    };

    function refetchContacts() {
        refetch();
        props.onContactsChanged();
    }

    useRefetchWhenPaneSaves(refetchContacts);

    function openContactPane(contact?: LegalEntityContactRow) {
        openPane(LegalEntityContactPane, { legalEntityId: props.legalEntityId!, contact });
    }

    function getMenuItems(model: LegalEntityContactRow) {
        let menuItems = [];

        if (!model.primaryContact) {
            menuItems.push({
                menuItem: translateString("setAsPrimaryContact"),
                onClick: () => {
                    setPrimaryContact(model);
                },
            });
        }

        menuItems.push({
            menuItem: translateString("edit"),
            onClick: () => openContactPane(model),
        });

        menuItems.push({
            menuItem: translateString("delete"),
            onClick: () => {
                setSelectedContact(model);
                setShowDeleteDialog(true);
            },
        });

        return menuItems;
    }

    return {
        isLoading,
        data,
        contacts: contacts ?? [],
        getFilterOptions,
        onFilterChange,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
        hasActiveFilters,
        getMenuItems,
        openContactPane,
        selectedContact,
        refetchContacts,
        showDeleteDialog,
        setShowDeleteDialog,
        hasContacts: isLoading || allRows.length > 0,
    };
}
